import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { OrganizationUpdatePayload } from "../../types/organization";
import axiosBaseURL from "..";

interface OrgParams {
  merchantType?: string;
  status?: string;
  page?: number;
  limit?: number;
  search?: string;
}

export const useUploadOrganizationDocument = () => {
  const { authString, user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { name: string; file: any; is_back: boolean }) => {
      const orgId = user?.mfiId;
      if (orgId) {
        const formData = new FormData();
        formData.append("document", data.file);
        formData.append("orgId", orgId);
        formData.append("pageNumber", "1");
        formData.append("documentType", data.name);
        formData.append("documentName", data.name);
        formData.append("isBack", "false");
        const result = await axiosBaseURL.post(
          `/off-taker/upload-document`,
          formData,
          {
            headers: {
              header: `${user?.userId}/${user?.token}`,
              Authorization: authString,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      }

      // return result;
      return;
    },
  });
};

export const useGetOrganizationDocuments = (orgId?: string | number) => {
  const { user } = useAuth() as AuthContextValue;
  const resolvedId = orgId ?? user?.mfiId;

  return useQuery({
    queryKey: ["documents", resolvedId],
    staleTime: 120_000,
    enabled: !!user?.token && !!resolvedId,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `off-taker/organization/${resolvedId}/documents`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useDeleteDocument = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (docId: number) => {
      const result = await axiosBaseURL.delete(
        `/off-taker/documents/${Number(docId)}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
};

export const useOrganizationsList = (options?: {
  merchantType?: string;
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "useOrganizationsList",
      options?.merchantType,
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.merchantType)
        queryParams.append("merchantType", options.merchantType);
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/off-taker/organizations?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
    enabled: !!user?.token,
  });
};

export const useGetBranchesList = (options?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["branch", options?.search, options?.page, options?.size].filter(
      Boolean,
    ),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.search) queryParams.append("search", options.search);

      const url = `/off-taker/branch?id=${
        user?.mfiId
      }&${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetBrancheById = (id?: string | number) => {
  return useQuery({
    queryKey: ["organizations", id],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      // const result = await axiosBaseURL.get(`/off-taker/organizations/${params}`, {
      //     headers: {
      //         header: `${user?.userId}/${user?.token}`,
      //         Authorization: authString,
      //     }
      // });

      return {
        status: "success",
        message: "",
        data: {
          id: 1,
          name: "Commercial Bank of Ethiopia",
          email: "example@cbe.com",
          phoneNumber: "0123456789",
          branchName: "Finfine Branch",
          address:
            '{"region": "Addis Ababa", "city": "Addis ababa", "kifle_ketema": "Arada", "woreda": "01"}',
          contactPerson: {
            id: 1,
            username: "dagtad",
            fullName: "Dagmawi Tadesse",
            roleName: "ROLE_KFT_SUPER_ADMIN",
            email: "dagtade@gmail.com",
            phone: "0911687411",
            organizationId: 1,
            status: "ACTIVE",
          },
          users: [
            {
              id: 1,
              username: "dagtad",
              fullName: "Dagmawi Tadesse",
              roleName: "ROLE_KFT_SUPER_ADMIN",
              email: "dagtade@gmail.com",
              phone: "0911687411",
              organizationId: 1,
              status: "ACTIVE",
            },
          ],
        },
      };
    },
  });
};

export const useGetOrganizationByName = (name?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["organization", name],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/off-taker/organization/name?name=${name}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useGetOrganizationByPhoneNumber = (phone?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["organization", phone],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/off-taker/organization/phone?number=${phone}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useGetOrganizationByEmail = (email?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["organization", email],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/off-taker/organization/email?email=${email}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useGetOrganizationDetail = (id?: number | string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["off-taker-organization", id],
    staleTime: 120_000,
    enabled:
      !!user?.token &&
      !!id &&
      id !== "0" &&
      String(id).length > 0,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/off-taker/organizations/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const { authString, user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (data: {
      id: number | string;
      payload: OrganizationUpdatePayload;
    }) => {
      const result = await axiosBaseURL.put(
        `/off-taker/organizations/${data.id}`,
        data.payload,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
            Authorization: authString,
          },
        },
      );

      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["off-taker-organization", variables.id],
      });
    },
  });
};

export const useUpdateOrganizationStatus = () => {
  const queryClient = useQueryClient();
  const { authString, user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (data: { id: string; status: string }) => {
      const result = await axiosBaseURL.put(
        `off-taker/organization/${data.id}?status=${data.status}`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
            Authorization: authString,
          },
        },
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["useOrganizationsList"] });
    },
  });
};

export const useCreateBranch = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name?: string;
      email?: string;
      phoneNumber?: string;
      address?: string;
      contactPerson: {
        fullName?: string;
        username?: string;
        password?: null;
        email?: string;
        phoneNumber?: string;
      };
    }) => {
      const result = await axiosBaseURL.post(`/off-taker/branch`, data, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branch"] });
    },
  });
};
